import { getCollectorOnCase } from '@debbie/common/dist/cases/index.js';
import { getContactPerson, } from '@debbie/common/dist/customers/index.js';
import { UnknownIntent } from '@debbie/common/dist/intents/index.js';
import { TemplateType } from '@debbie/common/dist/templates/index.js';
import { getMentionSuggestions } from '@debbie/common/dist/templates/suggestions.js';
import { useCollector } from '@debbie/cortex/dist/collectors/index.js';
import { useCreditor } from '@debbie/cortex/dist/creditors/index.js';
import { h } from '@debbie/cortex/dist/index.js';
import { useNewInteraction } from '@debbie/cortex/dist/newInteractions/index.js';
import * as types from '@debbie/cortex/dist/newInteractions/types.js';
import { usePropertyTypes } from '@debbie/cortex/dist/properties/index.js';
import { useTenant } from '@debbie/cortex/dist/tenant/index.js';
import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { encodeText } from '../../utils/templates.js';
import TemplateSelect from '../TemplateSelect.js';
const TEMPLATE_TYPE_TO_INTERACTION_KEY = {
    [TemplateType.EMAIL]: 'email',
    [TemplateType.LETTER]: 'letter',
    [TemplateType.SMS]: 'sms',
    [TemplateType.AUTOCALL]: 'autocall',
    [TemplateType.EBOKS]: 'eboks',
    [TemplateType.KIVRA]: 'kivra',
    [TemplateType.DIGIPOST]: 'digipost',
    [TemplateType.CREDITOR_MESSAGE]: 'note',
    [TemplateType.DEBTOR_MESSAGE]: 'note',
    [TemplateType.NOTE]: 'note',
};
// Channels whose interactions carry a subject alongside the content
const SUBJECT_INTERACTION_KEYS = new Set([
    'email',
    'eboks',
    'kivra',
    'digipost',
]);
const SUBJECT_TEMPLATE_TYPES = [
    TemplateType.EMAIL,
    TemplateType.EBOKS,
    TemplateType.KIVRA,
    TemplateType.DIGIPOST,
];
const INTERACTION_KEY_INITIAL_STATE = {
    email: types.emailInitialState,
    letter: types.letterInitialState,
    sms: types.smsInitialState,
    autocall: types.autocallInitialState,
    eboks: types.eboksInitialState,
    kivra: types.kivraInitialState,
    digipost: types.digipostInitialState,
    note: types.noteInitialState,
};
const TemplateSearch = (props) => {
    const singleCase = props.case;
    const [interaction, setInteraction] = useNewInteraction(singleCase.caseId);
    const creditorLoadable = useCreditor(singleCase?.creditorId);
    const creditor = h.hasData(creditorLoadable) ? creditorLoadable.data : null;
    const propertyTypesLoadable = usePropertyTypes();
    const propertyTypes = h.hasData(propertyTypesLoadable)
        ? propertyTypesLoadable.data
        : undefined;
    const collectorLoadable = useCollector((props.case &&
        creditor &&
        getCollectorOnCase(props.case.type, props.case.collectorId, creditor.identityCollectorId, creditor.defaultCollectorId)) ||
        undefined);
    const collector = h.hasData(collectorLoadable) ? collectorLoadable.data : null;
    const lang = useSelector((s) => s.lang);
    const tenant = useTenant();
    const contactPerson = getContactPerson(props.customer, props.case);
    const isAlternativeContact = contactPerson?.relation === 'alternative-contact';
    const collectorId = collector?.collectorId;
    const isTemplateForCase = useCallback((template) => {
        if (collectorId == null) {
            return false;
        }
        const isCreditorSame = template.creditorId === singleCase.creditorId || !template.creditorId;
        const isCollectorSame = collectorId === template.collectorId || !template.collectorId;
        return (isCreditorSame &&
            isCollectorSame &&
            isAlternativeContact === template.isForAlternativeContact);
    }, [collectorId, singleCase.creditorId, isAlternativeContact]);
    const mentionSuggestions = getMentionSuggestions(lang, props.templateType, propertyTypes || undefined, tenant);
    const interactionKey = TEMPLATE_TYPE_TO_INTERACTION_KEY[props.templateType];
    const selectTemplate = (template) => {
        const newContent = template.html
            ? encodeText(template.html || '', mentionSuggestions)
            : INTERACTION_KEY_INITIAL_STATE[interactionKey].content;
        const newSubject = template.subject
            ? encodeText(template.subject || '', mentionSuggestions)
            : types.emailInitialState.subject;
        const templateData = {
            content: newContent,
            templateName: template.name,
            templateId: template.templateId,
            templateLang: template.lang,
            templateFileName: template.fileName,
            intent: template.intent,
            actions: template.details.actions,
        };
        setInteraction(interactionKey, {
            ...interaction[interactionKey],
            ...templateData,
            ...(SUBJECT_INTERACTION_KEYS.has(interactionKey)
                ? { subject: newSubject }
                : {}),
        });
        if (props.onChange) {
            props.onChange(newContent, SUBJECT_TEMPLATE_TYPES.includes(props.templateType) ? newSubject : null, template.templateId);
        }
    };
    const removeTemplate = () => {
        const newContent = INTERACTION_KEY_INITIAL_STATE[interactionKey].content;
        const newSubject = types.emailInitialState.subject;
        const templateData = {
            content: newContent,
            templateName: '',
            templateId: '',
            templateLang: null,
            templateFileName: '',
            intent: UnknownIntent(),
            details: {},
        };
        setInteraction(interactionKey, {
            ...interaction[interactionKey],
            ...templateData,
            ...(SUBJECT_INTERACTION_KEYS.has(interactionKey)
                ? { subject: newSubject }
                : {}),
        });
        if (props.onChange) {
            props.onChange(newContent, newSubject, null);
        }
    };
    const stateInteraction = interaction[interactionKey];
    const selectedTemplateId = props.interaction?.templateId || stateInteraction?.templateId || null;
    return (React.createElement(TemplateSelect, { templateType: props.templateType, preferredLang: contactPerson?.lang || tenant?.lang, filter: isTemplateForCase, value: selectedTemplateId, selectedLang: stateInteraction.templateId === selectedTemplateId
            ? stateInteraction.templateLang
            : props.interaction?.templateLang, onChange: (template) => template != null ? selectTemplate(template) : removeTemplate() }));
};
export default TemplateSearch;
//# sourceMappingURL=TemplateSearch.js.map